import { useEffect, useRef, useState } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { getCampaigns } from '@medifind/interface';
import { toSlug, useUUID } from '@medifind/router';
import { useFeatureFlag } from '@medifind/shared-basic-components';
import { isPrerender, isTestSuite } from '@medifind/utils';
import { useAdSuppress, useAuthentication, useLocationStore, useRouteStore } from '@medifind/zustand';
import styles from './MedifindMoments.module.scss';
const isProd = process.env.NX_ENV === 'PROD';

const typeSize = {
  rectangle: [[300, 250]],
  'scrolling-rectangle': [[300, 250]],
  leaderboard: [
    [728, 90],
    [320, 50],
  ],
  default: [[0, 0]],
};
const getSlot = (id) => {
  return (
    window.googletag
      ?.pubads?.()
      ?.getSlots?.()
      ?.find((slot) => slot.getSlotElementId() === id) || null
  );
};
const useRect = (target) => {
  const [rect, setRect] = useState();
  useResizeObserver(target, (entry) => setRect(entry.contentRect));
  return rect;
};
const MediFindMoments = ({ type = '', classes = {}, style = {}, targeting, isSticky = false, onRendered }) => {
  const { adSuppress } = useAdSuppress();
  const [, setReceivedSlotResponse] = useState(null);
  const location = useLocationStore();
  const { countryCode, zipPostalCode, stateProvinceCode, city } = location;
  const isLoggedIn = useAuthentication((state) => state?.profile);
  const { pageType, position, specialties, ...targetingOptions } = targeting;
  const { params, routeUUID } = useRouteStore();
  const FEATURE_FLAG_SCROLLING_RECTANGLE = useFeatureFlag('SCROLLING_RECTANGLE');

  const adRef = useRef(null);

  const rect = useRect(adRef);
  const adSizes = typeSize[type] || typeSize.default;
  // Find a responsive size
  const responsiveAdSize = adSizes.find((s, i, arr) => {
    if (arr.length - 1 === i) return s;
    if (window.innerWidth - 16 * 2 < s[0]) return null; // Window size + 1rem margin
    const scale = Math.max(rect ? Math.round(Math.min(rect.width / s[0], 1) * 100) / 100 : 1, 0.1);
    if (scale >= 0.8) return s;
  });

  const { codingSlug, projectId, specialtySlug, ad_test: adTest, personId } = params;
  const uuid = useUUID();

  const { data: providerGroups, isFetched: checkedCampaigns } = useQuery({
    queryKey: ['campaignData', personId],
    queryFn: async () => {
      try {
        const { campaigns } = await getCampaigns({ personPersonifyId: personId });
        return campaigns;
      } catch (e) {
        return [];
      }
    },
    enabled: !!personId,
  });

  const targets = [
    ...Object.keys(targetingOptions).map((key) => {
      return { key, value: targetingOptions[key] };
    }),
    { key: 'isLoggedIn', value: !!isLoggedIn },
    { key: 'pageType', value: pageType },
    ...(codingSlug ? [{ key: 'conditionName', value: codingSlug }] : []),
    ...(specialtySlug ?? specialties ? [{ key: 'specialtyName', value: specialtySlug ?? specialties.map(toSlug) }] : []),
    ...(projectId ? [{ key: 'conditionId', value: projectId }] : []),
    ...(countryCode ? [{ key: 'countryCode', value: countryCode }] : []),
    ...(zipPostalCode ? [{ key: 'zipCode', value: zipPostalCode }] : []),
    ...(stateProvinceCode ? [{ key: 'stateProvinceCode', value: stateProvinceCode }] : []),
    ...(city ? [{ key: 'city', value: city }] : []),
    ...(providerGroups?.length > 0 ? [{ key: 'providerGroup', value: providerGroups }] : []),
  ];

  const slot = getSlot(uuid);
  const slotResponse = slot?.getResponseInformation() || null;

  useEffect(() => {
    if (!uuid || !routeUUID) return;
    // If we have a persoonId param, and the campaign fetch call hasnt returned yet then skip wait
    if (personId && !checkedCampaigns) {
      return;
    }
    // Or if the ad supression query param is turned on
    if (adSuppress || isTestSuite || isPrerender) return;

    const parentAdUnit = isProd && adTest == null ? `medifind` : `medifind-test`;

    const initializeGPT = () => {
      const slot = getSlot(uuid);
      if (!slot) {
        window.googletag.cmd.push(() => {
          const { pubads, enableServices, display, defineSlot } = window.googletag ?? {};
          pubads().enableAsyncRendering();
          pubads().collapseEmptyDivs(true);
          pubads().disableInitialLoad();
          // SRA isn't doing anything here as there is a race condition for other ads on the page
          // Should probably pause for 1-2 seconds before enabling services, that or accept that for speed we don't worry about SRA
          // pubads().enableSingleRequest();
          pubads().enableLazyLoad({
            // Fetch slots within 1 viewports.
            fetchMarginPercent: 100,
            // Render slots within 0.25 viewports.
            renderMarginPercent: 0,
            // Double the above values on mobile.
            mobileScaling: 1.5,
          });
          const newSlot = defineSlot(`/23178507926/${parentAdUnit}/${pageType}`, responsiveAdSize, uuid)
            .setForceSafeFrame(true)
            .setSafeFrameConfig({ sandbox: true })
            .addService(pubads())
            .setTargeting('position', position);
          if (targets.length > 0) {
            targets.forEach(({ key, value }) => {
              // key values have a 40 char limit in GAM
              newSlot.setTargeting(key, (value.length > 40 ? value.substring(0, 40) : value)?.toString());
            });
          }
          // window.onDvtagReady();
          enableServices();
          window.onDvtagReady(() => {
            display(uuid);
            pubads().refresh([newSlot]);
          }, 750);
          pubads().addEventListener('slotOnload', (event) => {
            const slotId = event.slot.getSlotElementId();
            if (uuid === slotId) {
              setReceivedSlotResponse(true);
            }
          });
          pubads().addEventListener('slotRenderEnded', (event) => {
            const slotId = event.slot.getSlotElementId();
            if (uuid === slotId && onRendered) {
              onRendered(event);
            }
          });
        });
      }
    };
    if (window.googletag?.cmd) {
      initializeGPT();
    } else {
      window.googletag = { cmd: [initializeGPT] };
    }
    // const unListen = listen(routeChange);
    // Clean up the slot when the component is unloaded.
    return () => {
      // unListen
      window.googletag?.cmd?.push(() => {
        if (window.googletag.pubads?.()) {
          const slotToDestroy = getSlot(uuid);
          if (slotToDestroy) {
            window.googletag.destroySlots([slotToDestroy]);
          }
        }
      });
    };
    // We dont want to include targetting values as this would cause ads to reload and change when changing locations / filters on a page, causing cls issues
    // Ensure whatever is added here does not cause a duplicate refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, responsiveAdSize, routeUUID, providerGroups]);

  const scale = Math.max(rect ? Math.round(Math.min(rect.width / responsiveAdSize[0], 1) * 100) / 100 : 1, 0.1);
  if (adSuppress || isTestSuite || isPrerender) return null;
  if (type === 'scrolling-rectangle' && !FEATURE_FLAG_SCROLLING_RECTANGLE) type = 'rectangle';
  return (
    <div
      className={classNames(styles['ads'], styles[`ads--${type}`], classes.ad, {
        [styles['displayed']]: slotResponse,
        [styles[`sticky`]]: isSticky,
      })}
      style={style.ad}
      ref={adRef}
    >
      <div className={classNames(styles['iframe-ad-wrapper'], classes.adWrapper)} style={style.adWrapper}>
        <div
          className={classNames(styles['ad-content'], classes.adContent)}
          id={uuid}
          style={
            slotResponse
              ? { width: responsiveAdSize[0] * scale, height: responsiveAdSize[1] * scale, '--ad-scale': scale, ...style.adContent }
              : style.adContent
          }
        >
          {slotResponse && <div className={classNames(styles['ad-label'], classes.adLabel)}>Ad</div>}
        </div>
      </div>
    </div>
  );
};

MediFindMoments.propTypes = {
  type: PropTypes.oneOf(['leaderboard', 'rectangle', 'scrolling-rectangle']).isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  classes: PropTypes.object,
  targeting: PropTypes.shape({
    position: PropTypes.string.isRequired,
    insurance: PropTypes.string,
    pageType: PropTypes.oneOf([
      // When adding a page type an ad unit must be added for both medifind and medifind-test parent ad units https://admanager.google.com/23178507926#inventory/ad_unit/list
      // Also ad the type to https://admanager.google.com/23178507926#inventory/custom_targeting/detail/custom_key_id=14473877
      'clinical-trial-article',
      'clinical-trial-search',
      'condition-overview',
      'condition-provider-search',
      'name-provider-search',
      'provider-profile',
      'provider-search',
      'publication-article',
      'publication-search',
      'second-opinion-results',
      'specialty-provider-search',
      'symptom-checker-results',
      'treatment-article',
      'treatment-search',
    ]).isRequired,
  }).isRequired,
  isSticky: PropTypes.bool,
  onRendered: PropTypes.func,
};

export { MediFindMoments as default, MediFindMoments };
